import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, RefreshCw, AlertTriangle, Target, Lightbulb, Scale, CalendarClock, Pencil, Check } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";

const fmtBRL = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

// ==================== VALOR PONDERADO DO PIPELINE ====================
export function ValorPonderadoCard({ funil, onReload }) {
  const [editando, setEditando] = useState(false);
  const [probs, setProbs] = useState({});
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (funil?.probabilidades) setProbs(funil.probabilidades); }, [funil]);
  if (!funil) return null;

  const salvar = async () => {
    setSalvando(true);
    try {
      await axios.put(`${API}/crm/probabilidades`, probs);
      toast.success("Probabilidades atualizadas");
      setEditando(false);
      onReload?.();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao salvar probabilidades")); }
    finally { setSalvando(false); }
  };

  const etapas = Object.entries(funil.ponderado_por_etapa || {});
  return (
    <Card className="border-0 shadow-sm border-l-4 border-l-[#C9A84C]" data-testid="valor-ponderado-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-[#0A1628] flex items-center gap-2">
          <Scale className="h-5 w-5 text-[#C9A84C]" />Pipeline Ponderado
          <button
            type="button"
            className="ml-auto text-slate-400 hover:text-[#0A1628] transition-colors"
            title="Editar probabilidades por etapa (admin)"
            onClick={() => setEditando(!editando)}
            data-testid="btn-editar-probabilidades"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </CardTitle>
        <p className="text-xs text-slate-500">Previsão realista: valor estimado × probabilidade de fechar por etapa</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-baseline gap-3">
          <p className="text-2xl font-bold text-[#C9A84C]" data-testid="pipeline-ponderado-total">{fmtBRL(funil.pipeline_ponderado)}</p>
          <p className="text-xs text-slate-400">de {fmtBRL(funil.pipeline_bruto)} brutos</p>
        </div>
        <div className="space-y-1.5">
          {etapas.map(([id, e]) => (
            <div key={id} className="flex items-center justify-between text-xs" data-testid={`ponderado-etapa-${id}`}>
              <span className="text-slate-600 truncate">{e.label}</span>
              <div className="flex items-center gap-2 shrink-0">
                {editando ? (
                  <Input
                    type="number" min="0" max="100"
                    className="h-6 w-16 text-xs px-1.5"
                    value={probs[id] ?? e.probabilidade}
                    onChange={(ev) => setProbs(p => ({ ...p, [id]: ev.target.value }))}
                    data-testid={`input-prob-${id}`}
                  />
                ) : (
                  <Badge variant="outline" className="text-[10px]">{e.probabilidade}%</Badge>
                )}
                <span className="font-mono text-slate-700 w-24 text-right">{fmtBRL(e.valor_ponderado)}</span>
              </div>
            </div>
          ))}
        </div>
        {editando && (
          <Button size="sm" className="w-full bg-[#0A1628] text-white mt-1" onClick={salvar} disabled={salvando} data-testid="btn-salvar-probabilidades">
            <Check className="h-4 w-4 mr-1" />{salvando ? "Salvando…" : "Salvar probabilidades"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== ANALISE IA DO FUNIL ====================
function LeadAchado({ a, destaque }) {
  return (
    <div className={`p-2.5 rounded-lg border space-y-0.5 ${destaque ? "bg-emerald-50/60 border-emerald-200" : "bg-amber-50/60 border-amber-200"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-[#0A1628]">{a.titulo}</span>
        {a.confianca != null && <span className="text-[10px] text-slate-400 ml-auto">confiança {a.confianca}%</span>}
      </div>
      <p className="text-xs text-slate-600">{a.motivo}</p>
      {a.acao_sugerida && (
        <p className="text-xs font-medium text-[#0A1628] flex items-start gap-1">
          <Target className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />{a.acao_sugerida}
        </p>
      )}
    </div>
  );
}

export function AnaliseIACRMDialog({ open, onClose, loading, analise, onAnalisar }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-analise-ia-crm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />Análise Inteligente do Funil
          </DialogTitle>
          {analise && !loading && (
            <DialogDescription className="text-xs">
              Gerada em {new Date(analise.gerada_em).toLocaleString("pt-BR")} · {analise.leads_analisados} leads ativos · modelo {analise.modelo}
            </DialogDescription>
          )}
        </DialogHeader>
        {loading && (
          <div className="py-10 text-center space-y-3" data-testid="ia-crm-loading">
            <Sparkles className="h-8 w-8 mx-auto text-purple-500 animate-pulse" />
            <p className="text-sm text-slate-500">A IA está priorizando seus leads e procurando oportunidades esquecidas…<br />Isso pode levar até 1 minuto.</p>
          </div>
        )}
        {!loading && analise && analise.existe !== false && (
          <div className="space-y-4">
            {analise.resumo && (
              <p className="text-sm p-3 rounded-lg bg-purple-50 border border-purple-100 text-slate-700" data-testid="ia-crm-resumo">{analise.resumo}</p>
            )}
            {(analise.leads_prioritarios || []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 flex items-center gap-1">
                  <Target className="h-3.5 w-3.5" />Leads prioritários — foco agora
                </p>
                {analise.leads_prioritarios.map((a, i) => <LeadAchado key={i} a={a} destaque />)}
              </div>
            )}
            {(analise.leads_esquecidos || []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />Leads esquecidos — risco de perda
                </p>
                {analise.leads_esquecidos.map((a, i) => <LeadAchado key={i} a={a} />)}
              </div>
            )}
            {(analise.recomendacoes || []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Lightbulb className="h-3.5 w-3.5" />Recomendações para o time
                </p>
                {analise.recomendacoes.map((r, i) => (
                  <p key={i} className="text-xs text-slate-600 pl-5">• {r}</p>
                ))}
              </div>
            )}
          </div>
        )}
        {!loading && (!analise || analise.existe === false) && (
          <p className="text-sm text-slate-400 p-6 text-center">Nenhuma análise gerada ainda.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={onAnalisar} disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-atualizar-analise-crm">
            <RefreshCw className="h-4 w-4 mr-1" />{analise && analise.existe !== false ? "Atualizar análise" : "Analisar agora"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== FOLLOW-UP AO MOVER ETAPA ====================
export function FollowUpDialog({ data, onClose, onConfirm }) {
  const [acao, setAcao] = useState("");
  const [dataAcao, setDataAcao] = useState("");
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    if (data) { setAcao(""); setDataAcao(""); setSalvando(false); }
  }, [data]);
  if (!data) return null;

  const confirmar = async () => {
    setSalvando(true);
    try { await onConfirm(acao, dataAcao); } finally { setSalvando(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="dialog-followup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A1628]">
            <CalendarClock className="h-5 w-5 text-[#C9A84C]" />Qual a próxima ação?
          </DialogTitle>
          <DialogDescription className="text-xs">
            "{data.lead?.titulo}" avançou para <b>{data.etapaLabel}</b>. Agende o follow-up para o lead não ficar esquecido.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label className="text-xs">Próxima ação</Label>
            <Input
              placeholder="Ex: ligar para agendar visita, enviar proposta…"
              value={acao}
              onChange={(e) => setAcao(e.target.value)}
              data-testid="input-followup-acao"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data</Label>
            <Input type="date" value={dataAcao} onChange={(e) => setDataAcao(e.target.value)} data-testid="input-followup-data" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="btn-followup-pular">Agora não</Button>
          <Button size="sm" className="bg-[#0A1628] text-white" onClick={confirmar}
            disabled={salvando || !acao.trim() || !dataAcao} data-testid="btn-followup-agendar">
            {salvando ? "Agendando…" : "Agendar follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
